import { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Document, Page, pdfjs } from 'react-pdf';
import Button from '../components/ui/Button';
import Card from '../components/ui/Card';
import Input from '../components/ui/Input';
import Alert from '../components/ui/Alert';
import { validateEmail, sendSignedDocumentNotification } from '../utils/emailService';
import { generateSignatureId } from '../utils/idGenerator';
import { fetchPublicDocument, markPublicDocumentAsSigned } from '../utils/publicDocumentService';
import './SignPage.css';

// Set up the worker for PDF.js
pdfjs.GlobalWorkerOptions.workerSrc = `//cdnjs.cloudflare.com/ajax/libs/pdf.js/${pdfjs.version}/pdf.worker.min.js`;

const SignPage = () => {
  const { documentId } = useParams();
  const navigate = useNavigate();
  const signatureCanvasRef = useRef(null);
  const documentContainerRef = useRef(null);
  
  const [document, setDocument] = useState(null);
  const [numPages, setNumPages] = useState(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const [signatureData, setSignatureData] = useState({});
  const [signerName, setSignerName] = useState('');
  const [signerEmail, setSignerEmail] = useState('');
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);
  const [loadingDocument, setLoadingDocument] = useState(true);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [activeField, setActiveField] = useState(null);
  const [showSignatureModal, setShowSignatureModal] = useState(false);
  const [showTextInputModal, setShowTextInputModal] = useState(false);
  const [pages, setPages] = useState([]);
  const [textInputs, setTextInputs] = useState({});
  const [checkboxStates, setCheckboxStates] = useState({});

  // Load document data
  useEffect(() => {
    const loadDocument = async () => {
      try {
        setLoadingDocument(true);
        setError(null);
        
        const doc = await fetchPublicDocument(documentId);
        
        // Extract recipients from metadata field if available
        if (doc.fields) {
          const metadataField = doc.fields.find(f => f.id === 'recipients-metadata');
          if (metadataField && metadataField.recipients) {
            doc.recipients = metadataField.recipients;
          }
        }
        
        // Check for fields parameter in URL
        const urlParams = new URLSearchParams(window.location.search);
        const fieldsParam = urlParams.get('fields');
        if (fieldsParam) {
          try {
            // Parse fields from URL parameter
            const parsedFields = JSON.parse(decodeURIComponent(fieldsParam));
            if (Array.isArray(parsedFields)) {
              // Merge fields from URL with existing fields
              doc.fields = [...(doc.fields || []), ...parsedFields.filter(f => !doc.fields?.some(existingField => existingField.id === f.id))];
              console.log('Added fields from URL parameter:', parsedFields.length);
            }
          } catch (parseError) {
            console.error('Error parsing fields from URL:', parseError);
          }
        }
        
        // Pre-fill signer email if it's in the URL
        const emailParam = urlParams.get('email');
        if (emailParam && validateEmail(emailParam)) {
          setSignerEmail(emailParam);
        } else if (doc.selfSignEmail) {
          // For self-sign documents, pre-fill with the owner's email
          setSignerEmail(doc.selfSignEmail);
        }
        
        setDocument(doc);
        setLoadingDocument(false);
      } catch (err) {
        console.error('Error loading document:', err);
        setError('Failed to load document. It may not exist or has been deleted.');
        setLoadingDocument(false);
      }
    };
    
    loadDocument();
  }, [documentId]);

  // Handle PDF document loading
  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPages(Array.from(new Array(numPages), (_, index) => index + 1));
  };

  // Handle signature drawing
  const startDrawing = (e) => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.beginPath();
    
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.moveTo(x, y);
    setIsDrawing(true);
  };

  const draw = (e) => {
    if (!isDrawing) return;
    
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    const rect = canvas.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    
    ctx.lineTo(x, y);
    ctx.stroke();
  };

  const stopDrawing = () => {
    if (!isDrawing) return;
    
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.closePath();
    setIsDrawing(false);
    
    // Save signature data
    if (activeField) {
      const signatureImg = canvas.toDataURL('image/png');
      setSignatureData(prev => ({
        ...prev,
        [activeField.id]: signatureImg
      }));
    }
  };

  const clearSignature = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    
    if (activeField) {
      setSignatureData(prev => {
        const newData = { ...prev };
        delete newData[activeField.id];
        return newData;
      });
    }
  };

  const initializeCanvas = () => {
    const canvas = signatureCanvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.strokeStyle = 'black';
  };

  // Initialize canvas when modal is shown
  useEffect(() => {
    if (showSignatureModal) {
      initializeCanvas();
    }
  }, [showSignatureModal]);

  // Handle field click
  const handleFieldClick = (field) => {
    setActiveField(field);
    
    if (field.type === 'signature' || field.type === 'initial') {
      setShowSignatureModal(true);
    } else if (field.type === 'checkbox') {
      // Toggle checkbox state
      setCheckboxStates(prev => ({
        ...prev,
        [field.id]: !prev[field.id]
      }));
      
      // Save checkbox state to signature data
      setSignatureData(prev => ({
        ...prev,
        [field.id]: !checkboxStates[field.id] ? 'checked' : null
      }));
    } else if (field.type === 'text') {
      // Show text input modal
      setShowTextInputModal(true);
    } else {
      setShowSignatureModal(true);
    }
  };
  
  // Handle text input save
  const handleTextInputSave = () => {
    if (activeField && textInputs[activeField.id]) {
      setSignatureData(prev => ({
        ...prev,
        [activeField.id]: textInputs[activeField.id]
      }));
    }
    setShowTextInputModal(false);
  };

  // Handle saving signature
  const handleSaveSignature = () => {
    setShowSignatureModal(false);
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    
    if (!signerName) {
      newErrors.signerName = 'Your name is required';
    }
    
    if (!signerEmail) {
      newErrors.signerEmail = 'Your email is required';
    } else if (!validateEmail(signerEmail)) {
      newErrors.signerEmail = 'Please enter a valid email address';
    }
    
    // Get the signer's email from the URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const signerEmailParam = urlParams.get('email');
    
    // Filter fields based on signature type and signer
    const visibleFields = document?.fields?.filter(field => {
      // Skip metadata fields
      if (field.isMetadata || field.id === 'recipients-metadata') return false;
      
      // Handle different signature types
      if (document.signatureType === 'self' || document.isSelfSign) {
        // Self-sign: all fields are required
        return true;
      } else if (document.signatureType === 'me_and_others') {
        // Me and Others: only fields for this recipient or owner
        if (!signerEmailParam) {
          // If no email specified, all fields are visible (for testing)
          return true;
        }
        
        // Check if this is the owner's field
        const isOwnerField = field.recipientIndex === 0 || 
                           (document.recipients && 
                            document.recipients[0] && 
                            field.recipientEmail === document.recipients[0].email);
        
        // Include if it's for this recipient or it's the owner and owner is signing
        return field.recipientEmail === signerEmailParam || 
              (isOwnerField && signerEmailParam === document.selfSignEmail);
      } else {
        // Others Only: only fields for this recipient
        if (!signerEmailParam) {
          // If no email specified, all fields are visible (for testing)
          return true;
        }
        
        // Only include fields assigned to this recipient
        return field.recipientEmail === signerEmailParam;
      }
    }) || [];
    
    // Check if all required visible fields have signatures
    const missingSignatures = visibleFields.filter(field => !signatureData[field.id]);
    
    if (missingSignatures.length > 0) {
      newErrors.signatures = 'Please sign all required fields';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    try {
      setIsLoading(true);
      setError(null);
      
      // Create signed data
      const signedData = {
        signerName,
        signerEmail,
        signedAt: new Date().toISOString(),
        signatures: signatureData,
        id: generateSignatureId(),
        // Add signature type information
        signatureType: document.signatureType || (document.isSelfSign ? 'self' : 'others_only')
      };
      
      console.log('Saving signature data:', signedData);
      console.log('Signature fields data:', signatureData);
      
      // Get the signer's email from the URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      const signerEmailParam = urlParams.get('email');
      
      // Determine if this is the final signature based on signature type
      let isFinalSignature = true;
      
      if (document.signatureType === 'me_and_others' || document.signatureType === 'others_only') {
        // For multi-party signing, check if all recipients have signed
        const allRecipients = document.recipients || [];
        
        // Get existing signatures
        const existingSignatures = document.signatures || [];
        
        // Add the current signature
        const allSignatures = [...existingSignatures, { signerEmail }];
        
        // Check if all recipients have signed
        const allSigned = allRecipients.every(recipient => 
          allSignatures.some(sig => sig.signerEmail === recipient.email)
        );
        
        isFinalSignature = allSigned;
      }
      
      // Mark document as signed using the public document service
      const updatedDoc = await markPublicDocumentAsSigned(documentId, signedData);
      console.log('Document updated with signatures:', updatedDoc);
      
      // Update local document state to prevent "Document Already Signed" message on redirect
      setDocument({
        ...updatedDoc,
        status: isFinalSignature ? 'signed' : 'partially_signed'
      });
      
      // Generate the view link for the signed document
      const viewLink = `${window.location.origin}/complete/${documentId}`;
      console.log('Generated view link:', viewLink);
      
      // Send notification to document owner and signer
      const recipientEmail = document.recipientEmail || document.recipient_email;
      if (recipientEmail) {
        try {
          console.log('Sending notification to document owner:', recipientEmail);
          console.log('Sending notification to signer:', signerEmail);
          
          // Get the document file data
          let fileData = null;
          if (document.file_data) {
            if (document.file_data.startsWith('STORAGE_URL:')) {
              // If it's a storage URL, we'll let the server fetch it
              fileData = document.file_data;
            } else {
              // If it's base64 data, pass it directly
              fileData = document.file_data;
            }
          } else if (document.fileData) {
            fileData = document.fileData;
          }
          
          const notificationResult = await sendSignedDocumentNotification({
            documentId,
            recipientEmail,
            signerEmail,
            viewLink,
            fileData,
            fileName: document.name || 'signed_document.pdf',
            isFinalSignature
          });
          
          console.log('Notification result:', notificationResult);
        } catch (emailError) {
          // Log the error but don't prevent the user from proceeding
          console.error('Error sending email notifications:', emailError);
          // We'll still show success even if email fails
        }
      } else {
        console.warn('No recipient email found in document, skipping notification');
      }
      
      // Show success message and don't redirect
      setSuccess(true);
      setIsLoading(false);
    } catch (err) {
      console.error('Error signing document:', err);
      setError('Failed to sign document. Please try again.');
      setIsLoading(false);
    }
  };

  // Handle decline signing
  const handleDecline = () => {
    // Implement decline functionality if needed
    navigate('/');
  };

  // If document is loading, show loading
  if (loadingDocument) {
    return (
      <div className="sign-page">
        <div className="container">
          <div className="loading-container">
            <div className="loading-spinner"></div>
            <p>Preparing document for signing...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="sign-page">
        <div className="container">
          <Alert 
            type="error" 
            title="Error" 
            message={error} 
          />
          <div className="text-center mt-lg">
            <Button onClick={() => navigate('/')}>
              Return to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  // If document is not yet loaded, show loading
  if (!document) {
    return (
      <div className="sign-page">
        <div className="container">
          <Alert 
            type="error" 
            title="Document Not Found" 
            message="The document you're looking for doesn't exist or has been deleted." 
          />
          <div className="text-center mt-lg">
            <Button onClick={() => navigate('/')}>
              Return to Home
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (document.status === 'signed') {
    return (
      <div className="sign-page">
        <div className="container">
          <Card className="success-card">
            <div className="success-icon">✓</div>
            <h2 className="success-title">Document Already Signed</h2>
            <p className="success-message">
              This document has already been signed and completed.
            </p>
            <div className="text-center mt-lg">
              <Button onClick={() => navigate(`/complete/${documentId}`)}>
                View Signed Document
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  if (success) {
    // Determine if this is the final signature based on signature type
    const isFinalSignature = document.signatureType === 'self' || document.isSelfSign || 
                            !document.recipients || document.recipients.length <= 1;
    
    return (
      <div className="sign-page">
        <div className="container">
          <Card className="success-card">
            <div className="success-icon">✓</div>
            <h2 className="success-title">
              {isFinalSignature ? "Document Fully Signed!" : "Document Partially Signed!"}
            </h2>
            <p className="success-message">
              {isFinalSignature ? 
                "Thank you for signing the document. All signatures have been collected and the document is now complete." :
                "Thank you for signing the document. Other parties still need to sign this document to complete the process."}
            </p>
            <p className="success-message">
              A copy has been sent to you and the document owner.
            </p>
            <div className="text-center mt-lg">
              <Button onClick={() => navigate(`/complete/${documentId}`)}>
                View Signed Document
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  // Check if this is a decline request
  const urlParams = new URLSearchParams(window.location.search);
  const isDecline = urlParams.get('decline') === 'true';

  if (isDecline) {
    return (
      <div className="sign-page">
        <div className="container">
          <Card className="decline-card">
            <h2 className="decline-title">Decline to Sign</h2>
            <p className="decline-message">
              Are you sure you want to decline signing this document?
            </p>
            <div className="decline-actions">
              <Button 
                variant="secondary" 
                onClick={() => navigate(`/sign/${documentId}`)}
              >
                Cancel
              </Button>
              <Button 
                onClick={handleDecline}
              >
                Confirm Decline
              </Button>
            </div>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="sign-page">
      <div className="container">
        <div className="sign-header">
          <h1 className="page-title">Sign Document: {document.name}</h1>
          <p className="page-subtitle">
            {document.signatureType === 'self' || document.isSelfSign ? 
              "Please sign this document in the designated areas" :
              document.signatureType === 'me_and_others' ?
              "Please sign your part of this document" :
              "Please review the document and sign in the designated areas"}
          </p>
          
          {/* Signature Type Indicator */}
          {document.signatureType && (
            <div className={`signature-type-indicator ${document.signatureType}`}>
              <div className="signature-type-icon">
                {document.signatureType === 'self' || document.isSelfSign ? '🖋️' : 
                 document.signatureType === 'me_and_others' ? '👥' : '📤'}
              </div>
              <div className="signature-type-label">
                {document.signatureType === 'self' || document.isSelfSign ? 'Self-Sign Document' : 
                 document.signatureType === 'me_and_others' ? 'Multi-Party Signing' : 
                 'Recipient Signing'}
              </div>
            </div>
          )}
        </div>
        
        {/* Signing Status Bar */}
        {document.recipients && document.recipients.length > 0 && (
          <div className="signing-status-bar">
            <h3 className="signing-status-title">Signing Status</h3>
            <div className="signing-status-list">
              {document.recipients.map((recipient, index) => {
                // Check if this recipient has signed
                const hasSigned = document.signatures && 
                                 document.signatures.some(sig => 
                                   sig.signerEmail === recipient.email);
                
                return (
                  <div key={index} className="signing-status-item">
                    <div 
                      className="signing-status-indicator" 
                      style={{ backgroundColor: hasSigned ? '#4CAF50' : '#FFC107' }}
                    >
                      {hasSigned ? '✓' : '⌛'}
                    </div>
                    <div className="signing-status-details">
                      <div className="signing-status-email">{recipient.email}</div>
                      <div className="signing-status-label">
                        {hasSigned ? 'Signed' : 'Waiting for signature'}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
        
        {errors.signatures && (
          <Alert 
            type="error" 
            message={errors.signatures} 
            dismissible={true} 
            onDismiss={() => setErrors(prev => ({ ...prev, signatures: null }))}
          />
        )}
        
        <div className="sign-content">
          <div className="document-preview" ref={documentContainerRef}>
            <Document
              file={document.file_data && document.file_data.startsWith('STORAGE_URL:') 
                ? document.file_data.substring('STORAGE_URL:'.length) 
                : document.fileData}
              onLoadSuccess={onDocumentLoadSuccess}
              loading={<div className="loading-spinner"></div>}
              error={<Alert type="error" message="Failed to load PDF document" />}
            >
              <div className="document-pages-container">
                {pages.map(pageNumber => (
                  <div key={pageNumber} className="document-page-wrapper">
                    <div className="document-page-overlay">
                      {document?.fields
                        ?.filter(field => {
                          // Skip metadata fields
                          if (field.isMetadata || field.id === 'recipients-metadata') return false;
                          
                          // Check if field is on this page
                          if (field.pageNumber !== pageNumber) return false;
                          
                          // Get the signer's email from the URL parameters
                          const urlParams = new URLSearchParams(window.location.search);
                          const signerEmail = urlParams.get('email');
                          
                          // Handle different signature types
                          if (document.signatureType === 'self' || document.isSelfSign) {
                            // Self-sign: show all fields
                            return true;
                          } else if (document.signatureType === 'me_and_others') {
                            // Me and Others: show fields for this recipient or owner
                            if (!signerEmail) {
                              // If no email specified, show all fields (for testing)
                              return true;
                            }
                            
                            // Check if this is the owner's field
                            const isOwnerField = field.recipientIndex === 0 || 
                                               (document.recipients && 
                                                document.recipients[0] && 
                                                field.recipientEmail === document.recipients[0].email);
                            
                            // Show if it's for this recipient or it's the owner and owner is signing
                            return field.recipientEmail === signerEmail || 
                                  (isOwnerField && signerEmail === document.selfSignEmail);
                          } else {
                            // Others Only: show fields for this recipient only
                            if (!signerEmail) {
                              // If no email specified, show all fields (for testing)
                              return true;
                            }
                            
                            // Only show fields assigned to this recipient
                            return field.recipientEmail === signerEmail;
                          }
                        })
                        .map(field => (
                          <div
                            key={field.id}
                            className={`signature-field signature-field-${field.type} ${signatureData[field.id] ? 'signed' : ''}`}
                            style={{
                              left: `${field.position.x}px`,
                              top: `${field.position.y}px`,
                              width: field.type === 'signature' ? '150px' : 
                                    field.type === 'initial' ? '60px' : 
                                    field.type === 'checkbox' ? '40px' : 
                                    field.type === 'text' ? '200px' : '120px',
                              height: field.type === 'signature' ? '60px' : 
                                     field.type === 'initial' ? '40px' : 
                                     field.type === 'checkbox' ? '40px' : '40px',
                            }}
                            onClick={() => handleFieldClick(field)}
                          >
                            {signatureData[field.id] ? (
                              field.type === 'date' ? (
                                <div className="date-value">{signatureData[field.id]}</div>
                              ) : field.type === 'text' ? (
                                <div className="text-value">{signatureData[field.id]}</div>
                              ) : field.type === 'checkbox' ? (
                                <div className={`checkbox-value ${signatureData[field.id] === 'checked' ? 'checked' : ''}`}>
                                  {signatureData[field.id] === 'checked' && '✓'}
                                </div>
                              ) : (
                                <img 
                                  src={signatureData[field.id]} 
                                  alt={`${field.type} field`} 
                                  className="signature-image"
                                />
                              )
                            ) : (
                              <div className="signature-placeholder">
                                {field.type === 'signature' ? 'Click to sign' : 
                                field.type === 'initial' ? 'Initial' : 
                                field.type === 'date' ? 'Date' :
                                field.type === 'text' ? 'Enter text' :
                                field.type === 'checkbox' ? 'Check' : 'Field'}
                              </div>
                            )}
                          </div>
                        ))}
                    </div>
                    <Page 
                      pageNumber={pageNumber} 
                      width={800}
                      className="document-page"
                    />
                  </div>
                ))}
              </div>
            </Document>
          </div>
          
          <div className="sign-sidebar">
            <Card className="sign-form-card">
              <h3 className="sign-form-title">Complete Your Signature</h3>
              
              <form onSubmit={handleSubmit} className="sign-form">
                <div className="form-group">
                  <Input
                    label="Your Name"
                    type="text"
                    id="signerName"
                    value={signerName}
                    onChange={(e) => setSignerName(e.target.value)}
                    placeholder="Enter your full name"
                    error={errors.signerName}
                    required
                  />
                </div>
                
                <div className="form-group">
                  <Input
                    label="Your Email"
                    type="email"
                    id="signerEmail"
                    value={signerEmail}
                    onChange={(e) => setSignerEmail(e.target.value)}
                    placeholder="Enter your email address"
                    error={errors.signerEmail}
                    required
                  />
                </div>
                
                <div className="sign-instructions">
                  <p>Click on each signature field in the document to sign.</p>
                  <p>All fields must be completed before submitting.</p>
                </div>
                
                <div className="sign-actions">
                  <Button 
                    type="submit" 
                    loading={isLoading}
                    disabled={isLoading}
                    fullWidth
                  >
                    Complete Signing
                  </Button>
                  
                  <Button 
                    type="button"
                    variant="secondary"
                    onClick={() => navigate(`/sign/${documentId}?decline=true`)}
                    fullWidth
                  >
                    Decline to Sign
                  </Button>
                </div>
              </form>
            </Card>
          </div>
        </div>
        
        {/* Signature Modal */}
        {showSignatureModal && (
          <div className="signature-modal-overlay">
            <div className="signature-modal">
              <div className="signature-modal-header">
                <h3>
                  {activeField?.type === 'signature' ? 'Draw Your Signature' : 
                   activeField?.type === 'initial' ? 'Draw Your Initials' : 'Enter Date'}
                </h3>
                <button 
                  className="signature-modal-close" 
                  onClick={() => setShowSignatureModal(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="signature-modal-content">
                {activeField?.type === 'date' ? (
                  <div className="date-input-container">
                    <p>Today's date will be automatically added:</p>
                    <div className="date-display">
                      {new Date().toLocaleDateString()}
                    </div>
                    <Button 
                      onClick={() => {
                        const dateString = new Date().toLocaleDateString();
                        setSignatureData(prev => ({
                          ...prev,
                          [activeField.id]: dateString
                        }));
                        setShowSignatureModal(false);
                      }}
                    >
                      Add Date
                    </Button>
                  </div>
                ) : (
                  <>
                    <div className="signature-canvas-container">
                      <canvas
                        ref={signatureCanvasRef}
                        width={500}
                        height={200}
                        className="signature-canvas"
                        onMouseDown={startDrawing}
                        onMouseMove={draw}
                        onMouseUp={stopDrawing}
                        onMouseLeave={stopDrawing}
                      />
                    </div>
                    
                    <div className="signature-modal-actions">
                      <Button 
                        variant="secondary" 
                        onClick={clearSignature}
                      >
                        Clear
                      </Button>
                      <Button 
                        onClick={handleSaveSignature}
                        disabled={!signatureData[activeField?.id]}
                      >
                        Save
                      </Button>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        )}
        
        {/* Text Input Modal */}
        {showTextInputModal && activeField && (
          <div className="signature-modal-overlay">
            <div className="signature-modal">
              <div className="signature-modal-header">
                <h3>Enter Text</h3>
                <button 
                  className="signature-modal-close" 
                  onClick={() => setShowTextInputModal(false)}
                >
                  ×
                </button>
              </div>
              
              <div className="signature-modal-content">
                <div className="text-input-container">
                  <input
                    type="text"
                    className="text-field-input"
                    value={textInputs[activeField.id] || ''}
                    onChange={(e) => setTextInputs(prev => ({
                      ...prev,
                      [activeField.id]: e.target.value
                    }))}
                    placeholder="Enter your text here"
                  />
                  
                  <div className="signature-modal-actions">
                    <Button 
                      variant="secondary" 
                      onClick={() => setShowTextInputModal(false)}
                    >
                      Cancel
                    </Button>
                    <Button 
                      onClick={handleTextInputSave}
                    >
                      Save
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SignPage;
